import { Link } from "react-router-dom";
import { GraduationCap, ArrowLeft } from "lucide-react";
import usePageMeta from "../../lib/usePageMeta";

export function PortalLegalShell({ title, subtitle, children }) {
  return (
    <div className="bg-[#FDFCF6] min-h-screen">
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between">
          <Link to="/academy/portal" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#4A6FA5] text-white flex items-center justify-center">
              <GraduationCap className="w-5 h-5" strokeWidth={1.75}/>
            </div>
            <div>
              <div className="font-heading text-slate-900 text-sm font-semibold leading-tight">CCCHAPS</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 leading-tight">Chaplain Training Academy</div>
            </div>
          </Link>
          <Link to="/academy/portal" className="text-sm text-slate-600 hover:text-[#4A6FA5] inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4"/> Portal
          </Link>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-14">
        <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold">CCCHAPS · Chaplain Training Academy</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-2">{title}</h1>
        {subtitle && <p className="mt-3 text-slate-600 leading-relaxed">{subtitle}</p>}
        <div className="mt-8 prose prose-slate max-w-none text-sm leading-relaxed [&_h2]:font-heading [&_h2]:text-slate-900 [&_h2]:text-lg [&_h2]:mt-8 [&_h2]:mb-2 [&_p]:text-slate-700 [&_ul]:text-slate-700 [&_ul]:mt-1 [&_li]:my-1 [&_a]:text-[#4A6FA5]">
          {children}
        </div>
        <p className="mt-10 text-[11px] text-slate-500">
          Last updated: February 14, 2026 · Questions? <a href="mailto:office@ccchaps.com" className="text-[#4A6FA5]">office@ccchaps.com</a> · <Link to="/contact" className="text-[#4A6FA5]">Contact page</Link>
        </p>
      </div>

      <PortalLegalFooter/>
    </div>
  );
}

export function PortalLegalFooter() {
  return (
    <footer className="border-t border-slate-200 bg-white py-8 text-center text-xs text-slate-500" data-testid="portal-footer">
      <div className="max-w-4xl mx-auto px-4 space-y-2">
        <div className="flex flex-wrap justify-center gap-x-3 gap-y-1">
          <Link to="/academy/portal/privacy" className="hover:text-[#4A6FA5]" data-testid="footer-privacy">Privacy</Link>
          <span aria-hidden="true">·</span>
          <Link to="/academy/portal/terms" className="hover:text-[#4A6FA5]" data-testid="footer-terms">Terms</Link>
          <span aria-hidden="true">·</span>
          <Link to="/academy/portal/accessibility" className="hover:text-[#4A6FA5]" data-testid="footer-accessibility">Accessibility</Link>
          <span aria-hidden="true">·</span>
          <Link to="/contact" className="hover:text-[#4A6FA5]" data-testid="footer-contact">Contact &amp; Support</Link>
        </div>
        <div>
          <Link to="/academy" className="hover:text-[#4A6FA5]">← Back to CCCHAPS Chaplain Training Academy</Link>
        </div>
      </div>
    </footer>
  );
}

// =====================  PRIVACY  =====================
export function PortalPrivacy() {
  usePageMeta({ title: "Privacy — Chaplain Training Academy | CCCHAPS", description: "How the CCCHAPS Chaplain Training Academy Portal handles learner information." });
  return (
    <PortalLegalShell
      title="Learner privacy statement"
      subtitle="How the CCCHAPS Chaplain Training Academy Portal collects, uses, and protects your information when you register for CTA-001 or a related course."
    >
      <h2>Information the Academy collects</h2>
      <p>When you create a learner account or use the Academy Portal, we collect only what is needed to deliver your training and issue a valid certificate:</p>
      <ul>
        <li>Your name and email address</li>
        <li>Optional organization and contact details you choose to provide</li>
        <li>Enrollment records — which course(s) you signed up for and when</li>
        <li>Course progress — sections and modules you have marked complete</li>
        <li>Quiz attempts and scores for the CTA-001 knowledge check</li>
        <li>Case-study completion status and the reflection responses you submit</li>
        <li>Course evaluations you submit at the end of a course</li>
        <li>Certificate records issued in your name</li>
        <li>Live-class registrations, cancellations, and attendance records</li>
        <li>Email communication logs (which transactional emails were sent to you and their delivery status)</li>
      </ul>

      <h2>Why we use this information</h2>
      <ul>
        <li>Create and manage your learner account and sign-in</li>
        <li>Deliver CTA-001 and future Academy courses</li>
        <li>Track your progress and course completion</li>
        <li>Issue and verify certificates</li>
        <li>Manage live Zoom classes, including registration, attendance, and access to the private Zoom join information</li>
        <li>Send necessary transactional course communications (registration confirmations, completion, class reminders)</li>
        <li>Improve the Academy training experience</li>
        <li>Maintain accurate administrative and certificate-verification records</li>
      </ul>

      <h2>How your password is handled</h2>
      <ul>
        <li>Your password is never visible to CCCHAPS staff or administrators.</li>
        <li>Passwords are stored using industry-standard one-way hashing.</li>
        <li>CCCHAPS cannot retrieve or display your plaintext password. If you forget it, we can only offer to reset it.</li>
      </ul>

      <h2>Learner-to-learner privacy</h2>
      <ul>
        <li>Learners cannot see one another's account information, quiz results, evaluations, reflection responses, or attendance records.</li>
        <li>Your case-study reflection responses are not published or shared with other learners.</li>
        <li>The private Zoom join URL, meeting ID, and passcode are shown only to a registered participant of that live session. Cancelling your registration removes access to the private Zoom information.</li>
        <li>Administrator access is limited to authorized CCCHAPS personnel and is used for legitimate course administration only.</li>
      </ul>

      <h2>Marketing consent</h2>
      <ul>
        <li>Transactional course-related communication (registration confirmations, class reminders, completion notices) is a required part of running the course. It is separate from optional marketing.</li>
        <li>Opting into future CCCHAPS news, updates, or newsletters is entirely voluntary. You may choose not to receive marketing communication when you register or at any time by contacting <a href="mailto:office@ccchaps.com">office@ccchaps.com</a>.</li>
      </ul>

      <h2>Data sharing</h2>
      <ul>
        <li>CCCHAPS does not sell learner data.</li>
        <li>We use trusted third-party service providers — for example, transactional email delivery, cloud hosting, and analytics related strictly to Academy operations. These providers process information only as needed to operate the platform on our behalf.</li>
        <li>Aside from these operational service providers, we do not share your Academy information with outside parties except where legally required.</li>
      </ul>

      <h2>Data retention and deletion</h2>
      <ul>
        <li>Course records and completion history (including your certificate number and completion date) may be retained for legitimate administrative and certificate-verification purposes.</li>
        <li>Authorized CCCHAPS administrators may remove learner records where appropriate, subject to any legitimate recordkeeping needs.</li>
        <li>If you have a question about your Academy account or the information we hold about you, please contact <a href="mailto:office@ccchaps.com">office@ccchaps.com</a> and we will respond as soon as we reasonably can.</li>
      </ul>

      <h2>Contact</h2>
      <p>
        Questions about learner privacy at the Chaplain Training Academy?{" "}
        <a href="mailto:office@ccchaps.com">office@ccchaps.com</a>{" "}·{" "}
        <Link to="/contact">CCCHAPS contact page</Link>{" "}·{" "}
        <a href="https://www.ccchaps.com" target="_blank" rel="noopener noreferrer">www.ccchaps.com</a>
      </p>
    </PortalLegalShell>
  );
}

// =====================  TERMS  =====================
export function PortalTerms() {
  usePageMeta({ title: "Terms — Chaplain Training Academy | CCCHAPS", description: "Terms for using the CCCHAPS Chaplain Training Academy Portal." });
  return (
    <PortalLegalShell
      title="Learner terms of use"
      subtitle="Everyday, plain-language terms for using the CCCHAPS Chaplain Training Academy Portal."
    >
      <h2>Your account</h2>
      <ul>
        <li>Your account and any live-class registration are for you personally. Please don't share your login or private Zoom join information with anyone else.</li>
        <li>You are responsible for keeping your password confidential.</li>
      </ul>

      <h2>What CTA-001 is and is not</h2>
      <ul>
        <li>CTA-001 is introductory workplace-chaplaincy educational training. It is designed to help you understand grief in the workplace and offer supportive presence.</li>
        <li>Completion does not confer professional licensure, clinical certification, accredited continuing-education credit, or authority to provide mental-health diagnosis or clinical treatment.</li>
        <li>Nothing in the course, its case study, or its resources is a substitute for professional mental-health care, medical advice, legal counsel, or crisis services.</li>
      </ul>

      <h2>Acceptable use</h2>
      <ul>
        <li>Please treat other learners, instructors, and CCCHAPS staff with respect.</li>
        <li>Do not attempt to interfere with the Academy Portal, live classes, or the accounts of other learners.</li>
        <li>Do not share, forward, or repost the private Zoom join URL, meeting ID, or passcode. That access is issued to your registration only.</li>
      </ul>

      <h2>Certificates</h2>
      <ul>
        <li>Certificates are issued to the name on your learner account. Please make sure your name is accurate before completing the course.</li>
        <li>CCCHAPS may verify certificates by certificate number. See the Privacy statement for how completion records are retained.</li>
      </ul>

      <h2>Changes and course availability</h2>
      <ul>
        <li>The Academy may adjust course content, live-class scheduling, or portal features to improve the learning experience.</li>
        <li>If a scheduled live class is cancelled, we will notify registered participants using the email address on their learner account.</li>
      </ul>

      <h2>Contact</h2>
      <p><a href="mailto:office@ccchaps.com">office@ccchaps.com</a> · <Link to="/contact">CCCHAPS contact page</Link></p>
    </PortalLegalShell>
  );
}

// =====================  ACCESSIBILITY  =====================
export function PortalAccessibility() {
  usePageMeta({ title: "Accessibility — Chaplain Training Academy | CCCHAPS", description: "Accessibility statement for the CCCHAPS Chaplain Training Academy Portal." });
  return (
    <PortalLegalShell
      title="Accessibility statement"
      subtitle="We want every learner to be able to participate in CTA-001 and future Academy courses."
    >
      <h2>Our commitment</h2>
      <p>The CCCHAPS Chaplain Training Academy Portal is designed to be usable by learners of a wide range of abilities. We aim to follow current web-accessibility good practice as we build and improve the portal.</p>

      <h2>What we try to do</h2>
      <ul>
        <li>Provide clear, readable text with sufficient color contrast against the background</li>
        <li>Support keyboard navigation for interactive elements such as forms, buttons, and menus</li>
        <li>Use form field labels so screen readers can announce inputs correctly</li>
        <li>Communicate status changes with icons + text, not color alone</li>
        <li>Layout that works on mobile (approximately 390px width) as well as tablet and desktop</li>
        <li>Alt text for meaningful images where they are used</li>
      </ul>

      <h2>Requesting an accommodation</h2>
      <p>When you register for a course, there is an optional "accessibility or accommodation request" field on the learner registration form. You can also reach us any time at <a href="mailto:office@ccchaps.com">office@ccchaps.com</a> to discuss:</p>
      <ul>
        <li>An alternative format for course materials</li>
        <li>Live captioning or interpretation for a live Zoom class</li>
        <li>Extra time or a different testing arrangement for the knowledge check</li>
        <li>Any other adjustment that would help you complete the course</li>
      </ul>

      <h2>Reporting an issue</h2>
      <p>If you encounter a barrier or something that does not work with your assistive technology, please tell us so we can fix it: <a href="mailto:office@ccchaps.com">office@ccchaps.com</a>. Please describe what happened, the page or feature involved, and the assistive technology or browser you were using if you know it.</p>

      <h2>Contact</h2>
      <p><a href="mailto:office@ccchaps.com">office@ccchaps.com</a> · <Link to="/contact">CCCHAPS contact page</Link></p>
    </PortalLegalShell>
  );
}
